import { API, azureClientId } from "config";

const fileApiBaseUrl = API.fileApi.url;

// Some browsers assign wrong or missing MIME types for these extensions — override them.
const CUSTOM_EXTENSION_MIME_TYPES: Record<string, string> = {
  ".r": "application/x-r",
  ".R": "application/x-r",
  ".py": "application/x-python",
  ".ts": "application/typescript", // browsers map .ts → video/mp2t (MPEG-2 Transport Stream)
  ".rs": "application/x-rust", // browsers map .rs → application/rls-services+xml
  ".ico": "image/x-icon", // browsers may map .ico → image/vnd.microsoft.icon (not in our allowlist)
};

export function resolveMimeType(filename: string): string {
  const ext = filename.slice(filename.lastIndexOf("."));
  return CUSTOM_EXTENSION_MIME_TYPES[ext] ?? "";
}

export interface UploadFileResponse {
  file_id: string;
  variants: FileVariant[];
  filename: string;
}

interface FileVariant {
  variant_id: string;
  variant_type: string;
}

interface StagingResponse {
  upload_url: string;
  staging_key: string;
  expires_in: number;
}

interface IngestResponse {
  file_id: string;
  variants: FileVariant[];
}

/**
 * Upload a file directly to file-api.
 *
 * This performs a 3-step process:
 * 1. Get staging URL from file-api
 * 2. Upload file bytes to presigned S3 URL
 * 3. Trigger ingest with text extraction
 *
 * @param file - The file to upload
 * @param threadId - The chat thread ID (used as retention scope)
 * @param onUploadProgress - Progress callback for tracking upload
 * @param getAccessToken - Function to get the user's access token
 * @returns Promise with file metadata (variants and filename)
 */
export async function uploadFile(
  file: File,
  threadId: string,
  onUploadProgress: (progressEvent: ProgressEvent) => void,
  getAccessToken: () => Promise<string>
): Promise<UploadFileResponse> {
  const accessToken = await getAccessToken();
  const headers = { Authorization: `Bearer ${accessToken}` };

  // Step 1: Get staging URL
  const stagingResponse = await fetch(
    `${fileApiBaseUrl}/api/v1/stage/singlepart/url`,
    {
      method: "POST",
      headers: {
        ...headers,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ expires_in: 300 }),
    }
  );

  if (!stagingResponse.ok) {
    const error = await stagingResponse.json().catch(() => ({}));
    throw new Error(error.message || "Failed to get staging URL");
  }

  const { upload_url, staging_key }: StagingResponse =
    await stagingResponse.json();

  // Step 2: Upload to staging (using XMLHttpRequest for progress tracking)
  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.onprogress = onUploadProgress;

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`));
      }
    };

    xhr.onerror = () => reject(new Error("Upload failed"));

    xhr.open("PUT", upload_url, true);
    xhr.send(file);
  });

  // Step 3: Ingest with text extraction
  const ingestResponse = await fetch(
    `${fileApiBaseUrl}/api/v1/scopes/${threadId}/finalize`,
    {
      method: "POST",
      headers: {
        ...headers,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        app_id: azureClientId,
        staging_key: staging_key,
        filename: file.name,
        mime_type:
          resolveMimeType(file.name) || file.type || "application/octet-stream",
      }),
    }
  );

  if (!ingestResponse.ok) {
    const error = await ingestResponse.json().catch(() => ({}));
    throw new Error(error.message || "Failed to ingest file");
  }

  const ingestResult: IngestResponse = await ingestResponse.json();

  return {
    file_id: ingestResult.file_id,
    variants: ingestResult.variants || [],
    filename: file.name,
  };
}
